<template>
  <v-app>
    <v-navigation-drawer class="elevation-16" app v-model="drawer" :permanent="openMenu" left v-if="isLoggedIn">
      <MainMenu />
    </v-navigation-drawer>

    <v-app-bar
      v-if="isLoggedIn"
      class="elevation-4"
      :class="initiator ? 'inverse-primary' : 'custom-primary'"
      app
      extension-height="1"
      dense
    >
      <v-app-bar-nav-icon @click.stop="(drawer = !drawer), closeLeftMenu()"></v-app-bar-nav-icon>
      <v-toolbar-title dense class=" headline mb-0 v-step-0">BYN:s Register</v-toolbar-title>

      <v-spacer></v-spacer>

      <SwitchAccountButton
        :visible="!!initiator"
        @click="switchAccount()"
      />

      <v-dialog
        v-model="showNotificationDialog"
        content-class="notification-dialog"
        hide-overlay
        scrollable
      >
        <template v-slot:activator="{on}">
          <v-btn @click="checkNotifications" icon v-on="on">
            <v-badge v-if="isLoggedIn" overlap color="red" :value="unreadNotificationCount">
              <template v-slot:badge>{{ unreadNotificationCount }}</template>
              <v-icon :style="{ fill: `${ initiator ? '#000' : '#fff'} !important` }">$sl-alarm</v-icon>
            </v-badge>
          </v-btn>
        </template>

        <NotificationDialog
          @close="showNotificationDialog = false"
          @readNotes="readNotifications"
          @showNotes="openNotificationPage"
        />
      </v-dialog>

      <ProgressBar :loading="isLoading" slot="extension" />
    </v-app-bar>

    <v-main>
      <v-container fluid class="no-margins">
        <router-view :key="$route.fullPath" />
      </v-container>
    </v-main>

    <CookieConsentDialog v-model="consentDialogOpen" />
  </v-app>
</template>

<script>
import API from '@/services/api.js'
import Auth from '@/services/auth'
import {mapGetters, mapActions} from 'vuex'
import MainMenu from '@/components/MainMenu'
//import RouteShortcuts from '@/components/RouteShortcuts'
import ProgressBar from '@/components/ProgressBar'
import NotificationDialog from '@/components/Notifications/NotificationDialog'
import { transform } from 'lodash'
import SwitchAccountButton from '@/components/admin/switch-account/SwitchAccountButton.vue'
import CookieConsentDialog from '../components/CookieConsentDialog.vue'
import { hasConsentChoice, getConsent, loadConsentScript } from '@/utils/consent'

// @group Common
// App component
export default {
  name: 'App',

  components: {
    MainMenu,
    //RouteShortcuts,
    ProgressBar,
    NotificationDialog,
    SwitchAccountButton,
    CookieConsentDialog
  },

  data: () => ({
    interval: null,
    showNotificationDialog: false,
    drawer: null,
    openMenu: false,
    consentDialogOpen: false,
  }),

  computed: {
    ...mapGetters('app', ['isLoading', 'apiVersion']),
    ...mapGetters('currentuser', ['isLoggedIn', 'notifications', 'unreadNotificationCount']),
    ...mapGetters('user', ['selectedInitiatorUser']),

    initiator() {
      return this.selectedInitiatorUser || localStorage.getItem('initiator_id')
    },
  },

  watch: {
    isLoggedIn(val) {
      this.pollNotifications(val)
    },
    initiator: {
      immediate: true,
      handler(initiator) {
        if ( initiator ) this.setInitiatorUser(initiator)
      }
    }
  },
  // @vuese
  //- Initialize API, Auth and store
  async created() {
    console.log('App.vue - created')
    this.pollNotifications(this.isLoggedIn)
  },

  // @vuese
  //- Here the app will show all messages
  mounted() {
    this.$events.listen('showError', (eventData, options) => this.showError(eventData, options))
    this.$events.listen('showInfo', (eventData, options) => this.showInfo(eventData, options))
    this.$events.listen('showWarning', (eventData, options) => this.showWarning(eventData, options))
    this.$events.listen('showSuccess', (eventData, options) => this.showSuccess(eventData, options))
    this.$events.listen('showAbout', (eventData, options) => this.showAbout(eventData, options))
    this.$events.listen('loggedOut', () => this.logout())
    this.$events.listen('openLeftMenu', eventData => this.openLeftMenu(eventData))
    this.$events.listen('closeLeftMenu', eventData => this.closeLeftMenu(eventData))

    if (!hasConsentChoice()) {
      this.consentDialogOpen = true
    } else if (getConsent() === 'accepted') {
      loadConsentScript()
    }
  },

  methods: {
    ...mapActions('currentuser', ['setCurrentUser', 'setNotifications']),
    ...mapActions('user', ['setInitiatorUser', 'clearInitiatorUser']),
    
    /**
     * @vuese
     * Used to read notifications if user is logged in
     * @arg The argument is an empty object
     */
    readNotifications() {
      if (this.isLoggedIn) {
        API.readNotifications({data: {}})
          .then(response => {
            this.loadNotifications()
          })
          .catch(error => {
            console.log('There was an error:', error)
          })
      }
    },
    /**
     * @vuese
     * Used to close the leftmenu when site tour is active and the breakpoint is mdAndDown.
     */
    closeLeftMenu(){
      this.openMenu = false
    },
    /**
     * @vuese
     * Used to open the leftmenu when site tour is active and the breakpoint is mdAndUp.
     */
    openLeftMenu() {
      this.openMenu = true
    },

    /**
     * @vuese
     * Used to push user to notifications
     */
    openNotificationPage() {
      this.showNotificationDialog = false
      if (this.$route && this.$route.name != 'Notifications') {
        this.$nextTick(() => {
          this.$router.push({name: 'Notifications', params: {loadTime: Date.now()}})
        })
      }
    },

    checkNotifications() {
      if (this.notifications.length === 0) {
        this.loadNotifications()
      }
    },
    /**
     * @vuese
     * Used to load notifications if user is logged in
     * @arg The argument is an object representing search criteries
     */
    loadNotifications() {
      if (this.isLoggedIn) {
        console.log('loadNotifications called')
        API.listNotifications({
          startAt: 0,
          pageSize: 100,
          sortBy: 'sentTime',
          sortDesc: true,
          search: ''
        })
          .then(response => {
            /**
             * @vuese
             * Used to update local store with the list of notifications
             * @arg The argument is list of notifications
             */
            this.setNotifications(response.data.rows)
          })
          .catch(error => {
            console.log('There was an error:', error)
          })
      }
    },

    pollNotifications(start) {
      if (start && process.env.VUE_APP_NOTIFICATION_POLL_INTERVAL) {
        this.interval = setInterval(
          function () {
            this.loadNotifications()
          }.bind(this),
          process.env.VUE_APP_NOTIFICATION_POLL_INTERVAL
        )
        return
      }
      if (this.interval) {
        this.stopNotificationsPoll()
      }
    },

    stopNotificationsPoll() {
      clearInterval(this.interval)
    },
    // @vuese
    //- App messages
    showError(message, timeout = 5000) {

      this.$dialog.notify.error(message, {position: 'top-right', timeout, outlined: true})
    },
    showInfo(message, timeout = 5000) {
      this.$dialog.notify.info(message, {position: 'top-right', timeout, outlined: true})
    },
    showWarning(message, timeout = 5000 ) {
      this.$dialog.notify.warning(message, {position: 'top-right', timeout, outlined: true})
    },
    showSuccess(message, timeout = 5000) {
      this.$dialog.notify.success(message, {position: 'top-right', timeout, outlined: true})
    },
    // @vuese
    //- Version
    showAbout() {
      this.$dialog.message.info(
        `Registret ${process.env.VUE_APP_VERSION}<br/><br/>Git: ${process.env.VUE_APP_GIT_RAW}<br/>API version: ${this.apiVersion}`,
        {position: 'top-right', timeout: 0}
      )
    },

    // @vuese
    //- Used to do a user logout
    logout() {
      this.drawer = false
      // @vuese
      //- Used to set current user to null
      this.setCurrentUser(null)
      // @vuese
      //- Used to set notification to an empty array
      this.setNotifications([])
      // @vuese
      //- Used to stop notifications
      this.stopNotificationsPoll()
      if (this.$route.path != '/') {
        this.$router.push('/')
      }
    },

    async switchAccount() {
      try {
        if ( this.initiator == null ) return
        const { data: targetUser } = await API.loginAsUserHash(this.initiator);
        this.setCurrentUser(targetUser)
        this.clearInitiatorUser()
        window.location.reload()
      } catch (error) {
        this.$events.fire('showError', 'Något gick snett.')
      }
    },
  }
}
</script>

<style>
/* Make tabs stick at top */
.fixed-tabs-bar {
  position: -webkit-sticky !important; /* Safari */
  position: sticky !important;
  top: 60px;
  z-index: 10;
}

/* Make drawer and top on top */
.v-navigation-drawer--fixed {
  z-index: 12;
}
.v-app-bar.v-app-bar--fixed {
  z-index: 11;
}

/* Cloak directive */
[v-cloak] {
  display: none;
}

/* Progressbar */
.v-toolbar__extension {
  padding: 0px !important;
}

.v-data-footer {
  justify-content: flex-end;
}

/* No select rows per page in footer */
.v-data-footer__select {
  display: none;
}

/**** TRANSITIONS ****/
.slide-fade-enter {
  transform: translateX(10px);
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s ease;
}

.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

.slide-enter {
  transform: translateX(10px);
}

.slide-enter-active,
.slide-leave-active {
  transition: all 5s ease;
}

.slide-leave-to {
  transform: translateX(-10px);
}

.fade-enter {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-leave-to {
  opacity: 0;
}
</style>
